// 登記取得専用画面（propertyCodeAcquisition）の API 契約 TS 型。
//
// 取得実行/リユース実行のリクエスト payload は utils/newAcquireRequest・
// reuseAcquireRequest の組み立て型（PropertyCodeNewAcquireRequest 等）を正とし、本ファイルでは
// レスポンス側のみを定義する。
import { type AcquireBookZenbujiko } from "@/types/acquirebook";

// ---- 共通 enum / union 型（レスポンスで使用するもののみ） ----

/**
 * 依頼ステータス（履歴行 status）。
 * BE が当該物件の明細のみから導出する。全件失敗時は FAILED を返す。
 */
export type RequestStatusCode =
  | "REQUESTING"
  | "SUCCESS"
  | "SOME_FAILED"
  | "FAILED";

/** セル内 status（種別なしセルは null）。 */
export type PictureCellStatus = "SUCCEEDED" | "FAILED" | "REQUESTING" | null;

// ---- ① 物件情報（画面ヘッダー）。GET /api/property-codes/{code}。 ----

/**
 * 物件種別（表示名文字列。enum value ではない）の取りうる値。
 * union 型（RealEstateType）と既知種別 Set（KNOWN_REAL_ESTATE_TYPES）の双方をここから導出し、
 * 種別追加時の二重管理（片方更新漏れ）を防ぐ単一情報源とする。
 */
export const REAL_ESTATE_TYPES = ["土地", "建物", "区分建物"] as const;

/** 物件種別（表示名文字列）。 */
export type RealEstateType = (typeof REAL_ESTATE_TYPES)[number];

/**
 * 既知の物件種別（表示名）の Set。
 * convertBookTypeToEng が未知種別を暗黙で LAND にフォールバックするため、その検知に使う。
 * REAL_ESTATE_TYPES から導出し、種別追加時の更新漏れ（Set だけ古いまま黙って Sentry 誤報）を防ぐ。
 */
export const KNOWN_REAL_ESTATE_TYPES = new Set<string>(REAL_ESTATE_TYPES);

export interface PropertyInfoResponse {
  /** 物件コード（UUID v5・不可逆ハッシュ）。フロントは解釈せずそのまま扱う。 */
  propertyCode: string;
  /** 物件種別（表示名文字列。enum value ではない）。 */
  realEstateType: RealEstateType;
  prefectureName: string;
  cityName: string;
  /** 大字（・丁目）。未確定時 null。 */
  ooaza: string | null;
  /** 地番/家屋番号。未取得時 null（マスク）。 */
  chibanOrKaoku: string | null;
  isAcquired: boolean;
}

// ---- ② 取得履歴一覧。GET /api/property-codes/{code}/picture-requests。 ----

/** 履歴行の各登記・図面セル。 */
export interface HistoryPictureCell {
  exists: boolean;
  /**
   * 当該種別 PDF の単一 DL 用 URL（acquireBookStatus の uri と同一扱い）。
   * DL 可能時に値、未存在（exists=false）/処理中（REQUESTING）/失敗（FAILED）時は null。
   * フロントは「uri が非 null かつ http/https スキーム」で DL ボタンの活性を判定する
   * （スキーム検証は DownloadCell の isSafeHttpUrl）。
   */
  uri: string | null;
  /** DL/参照用 ID。未存在時 null。 */
  pictureRequestDetailId: number | null;
  status: PictureCellStatus;
  /**
   * 登記取得日（法務局から PDF を受領した日・"YYYY-MM-DD"）。登記の鮮度を表す。
   * リユース取得では原本の登記取得日が引き継がれるため requestedAt より古くなりうる。
   * 未受領（REQUESTING/FAILED）・未存在（exists=false）時 null。
   */
  pictureReceivedDate: string | null;
  /**
   * 取得失敗時のエラー詳細（スクレイピング側の生メッセージ）。
   * 失敗（status=FAILED）時のみ値、それ以外（成功/処理中/未存在）は null。
   * 失敗セルの Tooltip で失敗理由として表示する。
   */
  errorDetail: string | null;
  /**
   * 取得時に選択された全部事項オプション（OptionType コード配列）。
   * 全部事項（zenbujiko）セルのみ値を持ち得る。他種別・未存在セルは空配列。
   * 現在事項は単独コードにならず目録コードの _GENZAI バリアントで表現される（取得 UI 仕様）。
   */
  options: AcquireBookZenbujiko[];
}

/** 取得履歴の 1 行（PictureRequest 依頼単位）。 */
export interface PropertyCodePictureRequestRow {
  /** 依頼ID（DL の起点）。 */
  pictureRequestId: number;
  /** ISO8601（JST, +09:00）。 */
  requestedAt: string;
  status: RequestStatusCode;
  zenbujiko: HistoryPictureCell;
  shoyushajiko: HistoryPictureCell;
  /** 地積測量図。 */
  chisekisokuryozu: HistoryPictureCell;
  /** 電子公図。 */
  denshikozu: HistoryPictureCell;
  /** 地役権図面。 */
  chiekikenzumen: HistoryPictureCell;
  /** 建物図面（土地物件では常に exists=false）。 */
  tatemonozumen: HistoryPictureCell;
  /** 企業共有スコープ由来か（API レスポンス項目。現在 UI では未使用）。 */
  isOtherUserResource: boolean;
}

/** BE は全件返却（API ページネーションなし）。 */
export interface PropertyCodePictureRequestListResponse {
  /** requestedAt 降順。0 件なら []。 */
  list: PropertyCodePictureRequestRow[];
}
